import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static values = {
    conversationId: Number,
    signedStreamName: String,
    autofocus: Boolean
  }
  static targets = [ "photoInput", "textarea", "previewContainer", "previewImage", "submit" ]

  connect() {
    this.lastTypedAt = 0
    this.typingThrottleMs = 3000
    this.subscribe()

    if (this.hasTextareaTarget) {
      this.resizeTextarea()
      this.updateSubmitButton()
      if (this.autofocusValue) this.textareaTarget.focus()
    }
  }

  disconnect() {
    this.unsubscribe()
  }

  subscribe() {
    if (!this.conversationIdValue || !this.signedStreamNameValue) return

    this.subscription = consumer.subscriptions.create(
      { channel: "ConversationChannel", signed_stream_name: this.signedStreamNameValue },
      { received: () => {} }
    )
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  pickPhoto() {
    this.photoInputTarget.click()
  }

  photoSelected() {
    const file = this.photoInputTarget.files[0]
    if (!file || !this.hasPreviewContainerTarget || !this.hasPreviewImageTarget) return

    const reader = new FileReader()
    reader.onload = (event) => {
      this.previewImageTarget.src = event.target.result
      this.previewContainerTarget.classList.remove("hidden")
      this.updateSubmitButton()
    }
    reader.readAsDataURL(file)
  }

  clearPhoto() {
    this.photoInputTarget.value = ""
    if (this.hasPreviewContainerTarget) {
      this.previewContainerTarget.classList.add("hidden")
      this.previewImageTarget.src = ""
    }
    this.updateSubmitButton()
  }

  resize() {
    this.resizeTextarea()
  }

  updateSubmitButton() {
    if (!this.hasSubmitTarget) return

    const hasText = this.hasTextareaTarget && this.textareaTarget.value.trim().length > 0
    const hasPhoto = this.hasPhotoInputTarget && this.photoInputTarget.files.length > 0
    this.submitTarget.disabled = !(hasText || hasPhoto)
  }

  photoChanged() {
    this.updateSubmitButton()
  }

  typing() {
    if (!this.subscription || !this.hasTextareaTarget) return

    if (this.textareaTarget.value.trim().length === 0) {
      this.stopTyping()
      return
    }

    const now = Date.now()
    if (now - this.lastTypedAt < this.typingThrottleMs) return

    this.lastTypedAt = now
    this.subscription.perform("typing", { conversation_id: this.conversationIdValue })
  }

  stopTyping() {
    if (!this.subscription) return
    if (this.lastTypedAt === 0) return

    this.subscription.perform("stop_typing", { conversation_id: this.conversationIdValue })
    this.lastTypedAt = 0
  }

  submitOnEnter(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      if (this.hasSubmitTarget && this.submitTarget.disabled) return
      this.element.requestSubmit()
    }
  }

  resizeTextarea() {
    const textarea = this.textareaTarget
    textarea.style.height = "auto"
    const lineHeight = 24
    const maxHeight = lineHeight * 4
    const newHeight = Math.min(textarea.scrollHeight, maxHeight)
    textarea.style.height = `${newHeight}px`
  }
}
